export type {
  DesignPage,
  DesignPageInventory,
  DesignAIWallet,
  DesignAIGrant,
  DesignAIUsage,
  DesignOrganization,
  DesignInvitation,
  DesignInvitationStatus,
  DesignInvitationDetails,
  DesignInvitationAccept,
  DesignCustomerActivation,
  DesignCustomerActivationInput,
  DesignSession,
  DesignHostingPortal,
  DesignWebsite,
  DesignWebsiteCreateInput,
  DesignSaveOperation,
  DesignSaveInput,
  DesignRelease,
  DesignPublishedSnapshot,
  DesignContent,
  DesignContentField,
  DesignChange,
  DesignConversation,
  DesignConversationEvent,
} from "@open-design/contracts";

function validationMessage(data: unknown): string | undefined {
  if (typeof data === "string") return data;
  if (Array.isArray(data))
    return (
      data.filter((value) => typeof value === "string").join(" ") || undefined
    );
  if (!data || typeof data !== "object") return undefined;
  const record = data as Record<string, unknown>;
  if (typeof record.detail === "string") return record.detail;
  for (const value of Object.values(record)) {
    if (typeof value === "string") return value;
    if (Array.isArray(value) && typeof value[0] === "string")
      return value.join(" ");
  }
  return undefined;
}

export async function platform<T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    org?: number;
    csrf?: string;
    authorization?: string;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const response = await fetch("/platform/api/" + path, {
    method: options.method ?? "GET",
    credentials: "same-origin",
    signal: options.signal,
    headers: {
      "Content-Type": "application/json",
      ...(options.org ? { "X-Org-ID": String(options.org) } : {}),
      ...(options.csrf ? { "X-Design-CSRF": options.csrf } : {}),
      ...(options.authorization
        ? { Authorization: options.authorization }
        : {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    throw Object.assign(
      new Error(
        validationMessage(data) ??
          (response.status === 401
            ? "Please sign in again."
            : "We could not complete this request."),
      ),
      { status: response.status },
    );
  }
  const body = await response.text();
  return body ? (JSON.parse(body) as T) : (undefined as T);
}

export function items<T>(value: T[] | { results: T[] }): T[] {
  return Array.isArray(value) ? value : value.results;
}

export async function uploadDesignAsset(
  websiteId: string,
  file: File,
  org: number,
  csrf: string,
): Promise<{ id: string }> {
  const body = new FormData();
  body.append("file", file);
  const response = await fetch(`/platform/api/websites/${websiteId}/assets/`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-Org-ID": String(org), "X-Design-CSRF": csrf },
    body,
  });
  if (!response.ok)
    throw new Error(
      "The file could not be uploaded. Use a supported file under 10 MiB.",
    );
  return response.json() as Promise<{ id: string }>;
}
